import type { TemplateType, EditableTypeType } from "hoxton-types";
import type { Campaign, MasterTemplate } from "@/types/types";

import type { DocumentNode, ApolloClient } from "@apollo/client";

import addMasterTemplateMutation from "@/apollo/mutations/v2/AddMasterTemplate.gql";
import associateMasterTemplateMutation from "@/apollo/mutations/v2/AssociateMasterTemplateWithCampaign.gql";
import disassociateMasterTemplateMutation from "@/apollo/mutations/v2/UnassociateMasterTemplateFromCampaign.gql";
import removeMasterTemplateMutation from "@/apollo/mutations/v2/RemoveMasterTemplate.gql";
import clientMasterTemplatesQuery from "@/apollo/queries/v2/ClientMasterTemplates.gql";
import updateTemplateMutation from "@/apollo/mutations/v2/UpdateMasterTemplate.gql";
import updateResizeTemplateMutation from "@/apollo/mutations/v2/UpdateResizedMasterTemplate.gql";
import completeAEMasterTemplates from "@/apollo/mutations/v2/CompleteAEMasterTemplate.gql";

interface AddedMasterTemplate {
    _id: string;
    adType: TemplateType;
    persistentKey: string;
}

export interface MasterTemplateAddResponse {
    previewMode: "GRANTED" | "RESOURCE_MANAGER";
    masterTemplate: AddedMasterTemplate | null;
    resource: {
        resourceId: string;
        status: string;
        percentage: number;
    } | null;
}

export interface MasterTemplateUpdateResponse {
    updateMasterTemplate: {
        jobId: string;
        usedInCampaigns?: Campaign[];
        manifestHashChanged?: boolean;
        producedAds?: { published: number; unpublished: number };
        editableGroupConflicts?: { added: string[]; removed: string[] };
        templateFamily?: MasterTemplate[];
    };
}

export interface MasterTemplateResizeUpdateResponse {
    updateResizedMasterTemplate: {
        jobId: string;
        updatedMasterTemplate?: MasterTemplate;
    };
}

type InputAELayer = {
    id: string;
    name: string;
    label: string;
    group: string;
    type: EditableTypeType;
    defaultValue?: string;
};

type InputAEComposition = {
    name: string;
    width: number;
    height: number;
    plainlyTemplateId: string;
    layers: InputAELayer[];
};

export default class MasterTemplates {
    /**
     * Creates new instance of MasterTemplate
     */
    private $apollo: ApolloClient<unknown>;
    private refreshQuery: DocumentNode | null;
    private defaults: {
        variables: { clientId: string };
        refetchQueries: { query: DocumentNode; variables: { clientId: string } }[];
    };

    constructor(apollo: ApolloClient<unknown>, clientId: string, refreshQuery: DocumentNode | null = null) {
        this.$apollo = apollo;
        this.refreshQuery = refreshQuery;

        this.defaults = {
            variables: {
                clientId
            },
            refetchQueries: []
        };

        if (refreshQuery) {
            this.defaults.refetchQueries = [
                {
                    query: refreshQuery,
                    variables: {
                        clientId: this.defaults.variables.clientId
                    }
                }
            ];
        } else {
            this.defaults.refetchQueries = [
                {
                    query: clientMasterTemplatesQuery,
                    variables: {
                        clientId: this.defaults.variables.clientId
                    }
                }
            ];
        }
    }

    /**
     * Uploads new master template
     */
    async add(zipUrl: string, campaignId?: string) {
        const result = await this.$apollo.mutate<{ addMasterTemplate: MasterTemplateAddResponse }>({
            mutation: addMasterTemplateMutation,
            variables: {
                zipUrl,
                clientId: this.defaults.variables.clientId,
                ...(campaignId && { campaignId })
            },
            refetchQueries: this.defaults.refetchQueries
        });

        if (!result.data) {
            throw new Error("No data returned from addMasterTemplate mutation");
        }

        return { data: result.data };
    }

    /**
     * Updates HTML5 master templates
     */
    async update(masterTemplateId: string, zipUrl: string, type: TemplateType | null, force = false, jobId?: string) {
        const result = await this.$apollo.mutate<MasterTemplateUpdateResponse>({
            mutation: updateTemplateMutation,
            variables: {
                masterTemplateId,
                zipUrl,
                clientId: this.defaults.variables.clientId,
                type,
                force,
                jobId
            }
        });

        if (!result.data) {
            throw new Error("No data returned from updateTemplate mutation");
        }

        return { data: result.data };
    }

    /**
     * Updates AE master templates with Plaily project data
     */
    async completeAE(masterTemplateId: string, plainlyProjectId: string, compositions: InputAEComposition[]) {
        const result = await this.$apollo.mutate<{ completeAEMasterTemplates: AddedMasterTemplate }>({
            mutation: completeAEMasterTemplates,
            variables: {
                initialTemplateId: masterTemplateId,
                plainlyProjectId,
                compositions,
                clientId: this.defaults.variables.clientId
            },
            refetchQueries: this.defaults.refetchQueries
        });

        if (!result.data) {
            throw new Error("No data returned from completeAEMasterTemplates mutation");
        }

        return { data: result.data };
    }

    async updateResize(masterTemplateId: string, parentId: string, jobId?: string) {
        const result = await this.$apollo.mutate<MasterTemplateResizeUpdateResponse>({
            mutation: updateResizeTemplateMutation,
            variables: {
                masterTemplateId,
                parentId,
                jobId
            }
        });

        if (!result.data) {
            throw new Error("No data returned from updateResizeTemplate mutation");
        }

        return { data: result.data };
    }

    async delete(masterTemplateId: string) {
        const result = await this.$apollo.mutate<{ removeMasterTemplate: { success: boolean } }>({
            mutation: removeMasterTemplateMutation,
            variables: {
                masterTemplateId
            },
            refetchQueries: this.defaults.refetchQueries
        });

        if (!result.data) {
            throw new Error("No data returned from removeMasterTemplate mutation");
        }

        return { data: result.data };
    }

    async associate(masterTemplateId: string, campaignId: string) {
        const result = await this.$apollo.mutate<{ associateMasterTemplateWithCampaign: { success: boolean } }>({
            mutation: associateMasterTemplateMutation,
            variables: {
                masterTemplateId,
                campaignId
            }
        });

        if (!result.data) {
            throw new Error("No data returned from associateMasterTemplate mutation");
        }

        return { data: result.data };
    }

    async disassociate(masterTemplateId: string, campaignId: string) {
        const result = await this.$apollo.mutate<{ unassociateMasterTemplateFromCampaign: { success: boolean } }>({
            mutation: disassociateMasterTemplateMutation,
            variables: {
                masterTemplateId,
                campaignId
            }
        });

        if (!result.data) {
            throw new Error("No data returned from disassociateMasterTemplate mutation");
        }

        return { data: result.data };
    }
}
